import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { MessageSquare, Send, Loader2 } from 'lucide-react'
import { useExchangeMessages, useSendMessage, type ExchangeMessage, type ExchangeLevel } from '../hooks/use-exchange-messages'
import { cn } from '@/shared/lib/utils'
import { NicolAIAvatar } from '@/shared/components'

interface ExchangeZoneProps {
  level: ExchangeLevel
  scopeId: string
  title?: string
  subtitle?: string
  className?: string
  embedded?: boolean
}

function formatTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday'
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }
}

function MessageBubble({ message }: { message: ExchangeMessage }) {
  const isEbuilder = message.is_from_ebuilder
  const authorName = message.author?.full_name || message.author?.email || 'Unknown'

  return (
    <div className={cn('flex gap-2', isEbuilder ? 'flex-row' : 'flex-row-reverse')}>
      {/* Avatar - only for HeyNicolai */}
      {isEbuilder && <NicolAIAvatar size="md" />}
      <div className={cn('flex flex-col gap-1', isEbuilder ? 'items-start' : 'items-end')}>
        <div className="flex items-center gap-2 text-[11px] text-foreground-muted">
          <span>{isEbuilder ? 'HeyNicolai' : authorName}</span>
          <span>·</span>
          <span>{formatTime(message.created_at)}</span>
        </div>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-3 py-2 text-sm',
          isEbuilder
            ? 'bg-accent text-white prose-invert'
            : 'bg-slate-100 text-foreground'
        )}
      >
        <div className="prose prose-sm max-w-none prose-p:my-0 prose-p:leading-relaxed prose-strong:font-semibold prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </div>
      </div>
      </div>
    </div>
  )
}

function groupMessagesByDate(messages: ExchangeMessage[]): Map<string, ExchangeMessage[]> {
  const groups = new Map<string, ExchangeMessage[]>()

  for (const message of messages) {
    const dateKey = formatDate(message.created_at)
    if (!groups.has(dateKey)) {
      groups.set(dateKey, [])
    }
    groups.get(dateKey)!.push(message)
  }

  return groups
}

export function ExchangeZone({ level, scopeId, title, subtitle, className, embedded = false }: ExchangeZoneProps) {
  const { t } = useTranslation()
  const [newMessage, setNewMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { data: messages = [], isLoading } = useExchangeMessages(level, scopeId)
  const sendMutation = useSendMessage()

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSend = () => {
    if (!newMessage.trim()) return

    sendMutation.mutate(
      { level, scopeId, content: newMessage.trim() },
      {
        onSuccess: () => {
          setNewMessage('')
        },
      }
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Get default title based on level
  const getDefaultTitle = () => {
    switch (level) {
      case 'team':
        return t('exchange.teamTitle')
      case 'feature':
        return t('exchange.featureTitle')
      case 'user_story':
        return t('exchange.usTitle')
      default:
        return t('exchange.title')
    }
  }

  const messageGroups = groupMessagesByDate(messages)
  const canSend = newMessage.trim().length > 0 && !sendMutation.isPending

  return (
    <div className={cn(
      'flex flex-col overflow-hidden bg-white',
      embedded ? 'h-full' : 'w-[400px] rounded-xl border border-border',
      className
    )}>
      {/* Header - hidden in embedded mode (parent provides header) */}
      <div className={cn('flex flex-col gap-1 border-b border-border px-5 py-4', embedded && 'hidden')}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-[18px] w-[18px] text-foreground" />
            <span className="text-sm font-semibold text-foreground">
              {title || getDefaultTitle()}
            </span>
          </div>
          <span className="rounded-full bg-background-secondary px-2 py-0.5 text-[11px] text-foreground-muted">
            {messages.length} {messages.length === 1 ? 'message' : 'messages'}
          </span>
        </div>
        {subtitle && (
          <p className="text-xs text-foreground-muted">{subtitle}</p>
        )}
      </div>

      {/* Messages Area */}
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-foreground-muted" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-center text-sm text-foreground-muted">
              {t('exchange.noMessages')}
            </p>
          </div>
        ) : (
          <>
            {Array.from(messageGroups.entries()).map(([date, msgs]) => (
              <div key={date} className="flex flex-col gap-3">
                {/* Date separator */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 border-t border-border" />
                  <span className="text-[11px] text-foreground-muted">{date}</span>
                  <div className="flex-1 border-t border-border" />
                </div>
                {/* Messages for this date */}
                {msgs.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="flex gap-3 border-t border-border p-4">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('exchange.messagePlaceholder')}
          disabled={sendMutation.isPending}
          className="flex-1 rounded-lg border border-border bg-background-secondary px-4 py-3 text-sm placeholder:text-foreground-subtle focus:border-accent focus:outline-none disabled:opacity-50"
        />
        <button
          onClick={handleSend}
          disabled={!canSend}
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-lg text-white transition-colors',
            canSend
              ? 'bg-accent hover:bg-accent/90'
              : 'bg-accent opacity-50'
          )}
        >
          {sendMutation.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Send className="h-5 w-5" />
          )}
        </button>
      </div>
    </div>
  )
}

// Convenience wrapper for backward compatibility
export function UserStoryExchangeZone({ userStoryId }: { userStoryId: string }) {
  return <ExchangeZone level="user_story" scopeId={userStoryId} />
}

export function FeatureExchangeZone({ featureId }: { featureId: string }) {
  return <ExchangeZone level="feature" scopeId={featureId} />
}

export function TeamExchangeZone({ teamId, subtitle, embedded }: { teamId: string; subtitle?: string; embedded?: boolean }) {
  return <ExchangeZone level="team" scopeId={teamId} subtitle={subtitle} embedded={embedded} />
}
